import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { Loader2 } from "lucide-react";
import { API } from "../../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Drill-down behind the P&L "External car wash" line.
 *
 * Reads `GET /accounting/external-car-wash` with the SAME period params
 * the P&L used, and that endpoint attributes each wash to the invoice
 * month exactly as `build_pnl` does — so `cost_booked` here equals the
 * figure on the page by construction, not by coincidence.
 */
export const ExternalCarWashDialog = ({ open, onClose, periodParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/accounting/external-car-wash`,
                                { params: periodParams });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load car wash costs");
    } finally {
      setLoading(false);
    }
  }, [periodParams]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const exportCsv = () => {
    const qs = new URLSearchParams(periodParams || {}).toString();
    window.open(`${API}/accounting/export/external-car-wash.csv?${qs}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto"
                     data-testid="external-car-wash-dialog">
        <DialogHeader>
          <DialogTitle>External car wash</DialogTitle>
          <DialogDescription className="text-slate-400">
            {data?.period?.label || "…"} — bought from an external company, so
            each wash is a cost of that job inside cost of sales. VAT is
            reclaimable only where the rate is above 0%.
          </DialogDescription>
        </DialogHeader>

        {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}

        {!loading && data && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div className="rounded border border-slate-700 bg-slate-800/50 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Washes</p>
                <p className="text-sm font-mono text-white" data-testid="wash-dialog-count">
                  {data.wash_count}
                </p>
              </div>
              <div className="rounded border border-cyan-500/40 bg-cyan-500/5 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Net cost</p>
                <p className="text-sm font-mono text-cyan-300" data-testid="wash-dialog-net">
                  {money(data.cost_net)}
                </p>
              </div>
              <div className="rounded border border-slate-700 bg-slate-800/50 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">VAT</p>
                <p className="text-sm font-mono text-white" data-testid="wash-dialog-vat">
                  {money(data.vat_amount)}
                </p>
              </div>
              <div className="rounded border border-slate-700 bg-slate-800/50 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Reclaimable VAT</p>
                <p className="text-sm font-mono text-emerald-300"
                   data-testid="wash-dialog-reclaimable">
                  {money(data.reclaimable_vat)}
                </p>
              </div>
              <div className="rounded border border-orange-500/40 bg-orange-500/5 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">
                  In cost of sales
                </p>
                <p className="text-sm font-mono text-orange-300"
                   data-testid="wash-dialog-booked">
                  {money(data.cost_booked)}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-cyan-300 mb-1">By rule / campaign</h4>
              {(data.by_rule || []).length === 0 ? (
                <p className="text-slate-500 text-sm py-2">No washes in this period.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 uppercase tracking-wide text-[10px]">
                      <th className="text-left py-1.5 pr-3">Rule</th>
                      <th className="text-right py-1.5 pr-3">Washes</th>
                      <th className="text-right py-1.5 pr-3">Net</th>
                      <th className="text-right py-1.5 pr-3">VAT</th>
                      <th className="text-right py-1.5">Booked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_rule.map((b, i) => (
                      <tr key={`${b.rule_name}-${i}`} className="border-t border-slate-800"
                          data-testid={`wash-dialog-rule-${i}`}>
                        <td className="py-1.5 pr-3 text-white">{b.rule_name}</td>
                        <td className="py-1.5 pr-3 text-right text-slate-300">{b.count}</td>
                        <td className="py-1.5 pr-3 text-right font-mono text-slate-300">
                          {money(b.cost_net)}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono text-slate-300">
                          {money(b.vat_amount)}
                        </td>
                        <td className="py-1.5 text-right font-mono text-orange-300">
                          {money(b.booked)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-300 mb-1">Jobs</h4>
              {(data.jobs || []).length === 0 ? (
                <p className="text-slate-500 text-sm py-2">
                  No car wash costs in this period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 uppercase tracking-wide text-[10px]">
                        <th className="text-left py-1.5 pr-3">Date</th>
                        <th className="text-left py-1.5 pr-3">Customer</th>
                        <th className="text-left py-1.5 pr-3">Service</th>
                        <th className="text-left py-1.5 pr-3">Technician</th>
                        <th className="text-right py-1.5 pr-3">Net</th>
                        <th className="text-right py-1.5 pr-3">VAT</th>
                        <th className="text-right py-1.5">Booked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.jobs.map((j, i) => (
                        <tr key={`${j.booking_id}-${i}`} className="border-t border-slate-800"
                            data-testid={`wash-dialog-job-${i}`}>
                          <td className="py-1.5 pr-3 text-slate-400 whitespace-nowrap">
                            {j.invoice_date}
                          </td>
                          <td className="py-1.5 pr-3 text-white">
                            {j.customer_name || j.booking_id}
                          </td>
                          <td className="py-1.5 pr-3 text-slate-400">{j.services || "—"}</td>
                          <td className="py-1.5 pr-3 text-slate-300">
                            {j.technician_name || "—"}
                          </td>
                          <td className="py-1.5 pr-3 text-right font-mono text-slate-300">
                            {money(j.cost_net)}
                          </td>
                          <td className="py-1.5 pr-3 text-right font-mono text-slate-400">
                            {j.vat_rate_percent}% · {money(j.vat_amount)}
                          </td>
                          <td className="py-1.5 text-right font-mono text-orange-300">
                            {money(j.booked)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <button type="button" onClick={exportCsv}
                    className="text-xs text-slate-400 hover:text-white underline"
                    data-testid="wash-dialog-export-btn">
              Download CSV for the accountant
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
